/**
 * Hook for resolving a player's kanban card from the board's card list
 * @remarks
 * This hook provides a clean way to get card IDs (and the card's tags) for
 * mutations without custom localStorage caching. It reads the board's cards
 * from the Apollo cache and finds the matching player's card.
 *
 * It is mounted ONCE PER PLAYER CARD, which is what makes its fetch policy
 * load-bearing — see the `fetchPolicy` comment inside the hook (SE-5631).
 * @example
 * ```tsx
 * const { cardId, isLoading, error } = usePlayerCardId(playerId, boardId);
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!cardId) return <ErrorMessage message="Card not found" />;
 *
 * // Use cardId in mutations
 * await addTagsMutation({ variables: { cardId, tagIds } });
 * ```
 */

import { type ApolloError } from "@apollo/client";
import { useMemo } from "react";

import { CARDS_PAGE_SIZE } from "@/features/player-kanban/constants";
import {
  type ListKanbanCardsQuery,
  useListKanbanCardsQuery,
} from "@/generated/graphql";

/**
 * A tag as it is selected by `ListKanbanCards` on a card.
 */
export type PlayerCardTag = NonNullable<
  ListKanbanCardsQuery["listKanbanCards"]["edges"][number]["node"]["kanbanCard"]
>["tags"][number];

/**
 * Return type for usePlayerCardId hook
 */
interface UsePlayerCardIdReturn {
  /**
   * The resolved card ID for the player on the board, or null if not found
   */
  cardId: string | null;

  /**
   * The tags on the player's card, as selected by `ListKanbanCards`.
   * Empty when the card is not on the board (or has no tags).
   */
  cardTags: PlayerCardTag[];

  /**
   * Loading state from Apollo query
   */
  isLoading: boolean;

  /**
   * Error from Apollo query, if any
   */
  error: ApolloError | undefined;
}

const NO_TAGS: PlayerCardTag[] = [];

/**
 * Resolves a player's kanban card from the board's card list
 * @param playerId - The player's ID
 * @param boardId - The kanban board's ID
 * @returns Object containing cardId, the card's tags, loading state, and error
 */
export function usePlayerCardId(
  playerId: string,
  boardId: string
): UsePlayerCardIdReturn {
  // SE-5631: `cache-first`, NOT `cache-and-network`. This hook mounts once per
  // player card, so a network-touching policy turns one board mount into a
  // request per card: measured at 2 requests per card on a staggered mount
  // (Apollo's in-flight deduplication only collapses cards that mount in the
  // SAME commit, which a virtualized or incrementally revealed board does not
  // do). The board-level read in `useKanbanBoardQueries` is the one that owns
  // fetching this list — it is `cache-first` with the same variables, and tag
  // mutations write the same normalized `KanbanCard` entities — so every card
  // here is a cache reader that stays reactive without adding a round-trip.
  //
  // `first` matches CARDS_PAGE_SIZE used by useKanbanBoardQueries so all call
  // sites collapse into ONE normalized cache entry instead of fragmenting per
  // variable set (SE-5058).
  const { data, loading, error } = useListKanbanCardsQuery({
    variables: {
      kanbanBoardId: boardId,
      first: CARDS_PAGE_SIZE,
    },
    fetchPolicy: "cache-first",
    // Without a board there is no card list to resolve against, so the request
    // could only ever come back empty — and an unguarded hook would still send
    // it with `kanbanBoardId: ""`. Same guard `useTagMutations` sets.
    skip: !boardId,
  });

  const matchingCard = useMemo(() => {
    if (!data?.listKanbanCards?.edges) {
      return null;
    }

    // Find the card that matches the playerId
    const matchingEdge = data.listKanbanCards.edges.find(
      edge => edge.node.player.id === playerId
    );

    return matchingEdge?.node.kanbanCard ?? null;
  }, [data, playerId]);

  return {
    cardId: matchingCard?.id ?? null,
    cardTags: matchingCard?.tags ?? NO_TAGS,
    isLoading: loading,
    error,
  };
}
